/**
 * FeatureFlagProvider — loads feature flags via GraphQL and provides
 * resolution context for useFeatureFlag().
 *
 * Placed inside AuthLayout so we have the authenticated user/org context.
 * Fetches flags + overrides on mount and re-fetches on window focus.
 *
 * @example
 * // In App tree (inside AuthProvider + GraphQLProvider):
 * <FeatureFlagProvider>
 *   <AppRoutes />
 * </FeatureFlagProvider>
 */

import type {
  FeatureFlag,
  FeatureFlagOverride,
  UpdateChannel,
} from '@zoobook/shared/hooks/useFeatureFlag';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth/hooks';
import { graphqlClient, setAccessToken } from '../graphql/client';
import {
  FEATURE_FLAGS_QUERY,
  ORG_SETTINGS_QUERY,
  USER_PREFERENCES_QUERY,
} from '../graphql/queries/core';

/** Raw flag row from GraphQL (snake_case → camelCase via PostGraphile) */
interface FlagNode {
  id: string;
  key: string;
  name: string;
  status: string;
  platform: string | null;
  description: string | null;
  moduleKey: string | null;
}

/** Raw override row from GraphQL */
interface OverrideNode {
  id: string;
  featureFlagId: string;
  orgId: string | null;
  userId: string | null;
  enabled: boolean;
  reason: string | null;
}

export interface FeatureFlagContextValue {
  /** All feature flags from the DB */
  flags: FeatureFlag[];
  /** Per-zoo/user overrides applicable to the current user */
  overrides: FeatureFlagOverride[];
  /** The zoo's configured update channel (from org_settings) */
  zooChannel: UpdateChannel;
  /** Whether the current user has beta_tester enabled */
  isBetaTester: boolean;
  /** True while flags are being loaded for the first time */
  loading: boolean;
  /** Force re-fetch of flags (e.g. after admin changes) */
  refetch: () => void;
  /** Raw flag nodes including metadata (for admin dashboard) */
  rawFlags: FlagNode[];
  /** Raw override nodes (for admin dashboard) */
  rawOverrides: OverrideNode[];
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

/**
 * Access the feature flag context. Must be used inside FeatureFlagProvider.
 *
 * @throws Error if used outside provider (developer mistake)
 */
export function useFeatureFlagContext(): FeatureFlagContextValue {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error('useFeatureFlagContext must be used within <FeatureFlagProvider>');
  }
  return ctx;
}

/**
 * Provider that loads feature flags, overrides, and zoo/user context
 * from PostGraphile GraphQL on mount and on window focus.
 */
export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const [rawFlags, setRawFlags] = useState<FlagNode[]>([]);
  const [rawOverrides, setRawOverrides] = useState<OverrideNode[]>([]);
  const [zooChannel, setZooChannel] = useState<UpdateChannel>('stable');
  const [isBetaTester, setIsBetaTester] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  const fetchFlags = useCallback(async () => {
    if (!session) return;

    // Ensure token is set before querying
    setAccessToken(session.access_token);

    try {
      const [flagsResult, orgResult, prefsResult] = await Promise.all([
        graphqlClient.query(FEATURE_FLAGS_QUERY, {}, { requestPolicy: 'network-only' }).toPromise(),
        graphqlClient.query(ORG_SETTINGS_QUERY, {}, { requestPolicy: 'cache-first' }).toPromise(),
        graphqlClient
          .query(
            USER_PREFERENCES_QUERY,
            { userId: session.user.id },
            { requestPolicy: 'cache-first' },
          )
          .toPromise(),
      ]);

      if (flagsResult.data) {
        const flagNodes: FlagNode[] = flagsResult.data.allFeatureFlags?.nodes ?? [];
        const overrideNodes: OverrideNode[] = flagsResult.data.allFeatureFlagOverrides?.nodes ?? [];
        setRawFlags(flagNodes);
        setRawOverrides(overrideNodes);
      }

      const orgSettings = orgResult.data?.allOrgSettings?.nodes?.[0];
      if (orgSettings?.updateChannel) {
        setZooChannel(orgSettings.updateChannel as UpdateChannel);
      }

      const userPrefs = prefsResult.data?.allUserPreferences?.nodes?.[0];
      if (userPrefs) {
        setIsBetaTester(userPrefs.betaTester === true);
      }
    } catch (err) {
      // Graceful degradation: keep defaults (no flags, stable channel)
      console.error('[FeatureFlagProvider] Failed to fetch flags:', err);
    } finally {
      setLoading(false);
    }
  }, [session]);

  // Fetch on mount (once per session)
  useEffect(() => {
    if (!session || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchFlags();
  }, [session, fetchFlags]);

  // Re-fetch on window focus (tab switch back)
  useEffect(() => {
    function handleFocus() {
      if (session) fetchFlags();
    }
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [session, fetchFlags]);

  // Map raw nodes to the shared FeatureFlag/FeatureFlagOverride interfaces
  const flags = useMemo<FeatureFlag[]>(
    () =>
      rawFlags.map((f) => ({
        key: f.key,
        status: f.status as FeatureFlag['status'],
      })),
    [rawFlags],
  );

  // Find the override applicable to the current user:
  // 1. User-specific override (userId matches)
  // 2. Org-wide override (orgId matches, userId is null)
  const overrides = useMemo<FeatureFlagOverride[]>(() => {
    const userId = session?.user.id;
    // Build a map: flagKey → best override
    const flagIdToKey = new Map(rawFlags.map((f) => [f.id, f.key]));
    const result: FeatureFlagOverride[] = [];

    for (const o of rawOverrides) {
      const flagKey = flagIdToKey.get(o.featureFlagId);
      if (!flagKey) continue;

      // User-specific override takes precedence
      if (o.userId === userId) {
        result.push({ flagKey, enabled: o.enabled });
      } else if (o.userId === null) {
        // Org-wide override (only if no user-specific override for this flag)
        if (!result.some((r) => r.flagKey === flagKey)) {
          result.push({ flagKey, enabled: o.enabled });
        }
      }
    }

    return result;
  }, [rawFlags, rawOverrides, session?.user.id]);

  const refetch = useCallback(() => {
    fetchFlags();
  }, [fetchFlags]);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({
      flags,
      overrides,
      zooChannel,
      isBetaTester,
      loading,
      refetch,
      rawFlags,
      rawOverrides,
    }),
    [flags, overrides, zooChannel, isBetaTester, loading, refetch, rawFlags, rawOverrides],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}
